// Library methods
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Box, Grid, Typography } from "@mui/material";

const Synoptophore = ({ examData }) => {
  // internationalization
  const { t } = useTranslation();

  const currentQuestion = useMemo(
    () => examData?.state?.currentQuestion,
    [examData?.state]
  );

  const currentSlideSet = useMemo(
    () => examData?.state?.currentSlideSet,
    [examData?.state]
  );

  const eyeAlignmentResults = useMemo(
    () =>
      examData?.state?.responses?.filter((response) =>
        response?.questionType?.includes("EyeAlignment")
      ),
    [examData?.state]
  );

  const fusionResults = useMemo(
    () =>
      examData?.state?.responses?.filter((response) =>
        response?.questionType?.includes("Fusion")
      ),
    [examData?.state]
  );

  const stereopsisResults = useMemo(
    () =>
      examData?.state?.responses?.filter((response) =>
        response?.questionType?.includes("Stereopsis")
      ),
    [examData?.state]
  );

  const getFormattedResponse = useCallback(
    (responseData) => {
      // only return the option number
      if (responseData?.includes("Option")) {
        return responseData?.split("_")?.[1];
      }
      return t(`word_${responseData?.toLowerCase()}`);
    },
    [t]
  );

  return (
    <Box
      height="100%"
      width="100%"
      display="flex"
      flexDirection="column"
      gap="20px"
    >
      {/* Current Left / Right Eye Slides */}
      {!currentQuestion?.questionType?.includes("EyeAlignment") && (
        <Box
          display="flex"
          justifyContent="space-around"
          sx={{ width: "100%", height: "100%" }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="8px"
            sx={{ width: "100%", height: "100%" }}
          >
            <Typography
              fontSize="14px"
              fontWeight={600}
              sx={{ textTransform: "capitalize" }}
            >
              {t("left_eye_image")}
            </Typography>
            <img
              alt="current left eye slide"
              height="100%"
              width="50%"
              style={{
                borderRadius: "50%",
                border: "1px solid black",
              }}
              src={`data:image/png;base64,${currentSlideSet?.leftEyeImage}`}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="8px"
            sx={{ width: "100%", height: "100%" }}
          >
            <Typography
              fontSize="14px"
              fontWeight={600}
              sx={{ textTransform: "capitalize" }}
            >
              {t("right_eye_image")}
            </Typography>
            <img
              alt="current right eye slide"
              height="100%"
              width="50%"
              style={{
                borderRadius: "50%",
                border: "1px solid black",
              }}
              src={`data:image/png;base64,${currentSlideSet?.rightEyeImage}`}
            />
          </Box>
        </Box>
      )}

      {/* Current Question and Options */}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap="8px"
        border="1px solid black"
        borderRadius="8px"
        p="20px"
      >
        <Typography textAlign="center" fontWeight={600}>
          {t("current_question")}: {t(currentQuestion?.questionType)}
        </Typography>
        {!currentQuestion?.questionType?.includes("SimultaneousPerception") && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            gap="8px"
          >
            {currentQuestion?.options?.map((currentOption, index) => (
              <Typography textAlign="center">
                <span style={{ fontWeight: 600 }}>{index + 1}.</span>{" "}
                {getFormattedResponse(currentOption)}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      {/* Past Eye Alignment Responses */}
      {eyeAlignmentResults && eyeAlignmentResults?.length > 0 && (
        <Box
          border="1px solid black"
          borderRadius="8px"
          p="20px"
          sx={{ width: "100%", height: "100%" }}
        >
          <Typography mb="20px">{t("word_eye_alignment")}</Typography>
          <Box display="flex" flexDirection="column" gap="20px">
            <Grid
              container
              display="flex"
              justifyContent="space-around"
              rowGap="8px"
              p="8px"
            >
              {eyeAlignmentResults?.map((response, index) => {
                const isCorrect =
                  response.responseData === response.expectedResponse;
                return (
                  <Grid
                    item
                    xs={12}
                    key={response.questionType}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap="4px"
                  >
                    <Typography textAlign="center" fontWeight={600}>
                      {index + 1}. {t(response.questionType)}
                    </Typography>
                    <Typography textAlign="center">
                      {`${t("word_response")}: ${getFormattedResponse(response.responseData)}`}
                    </Typography>
                    {response.expectedResponse && (
                      <Typography
                        textAlign="center"
                        color={isCorrect ? "#13A321" : "#B21515"}
                      >
                        {`${t("word_expected_response")}: ${getFormattedResponse(response.expectedResponse)}`}
                      </Typography>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      )}

      {/* Past Fusion Responses */}
      {fusionResults?.length > 0 && (
        <Box
          border="1px solid black"
          borderRadius="8px"
          p="20px"
          sx={{ width: "100%", height: "100%" }}
        >
          <Typography mb="20px">{t("word_fusion_results")}</Typography>
          <Box display="flex" flexDirection="column" gap="20px">
            <Grid
              container
              display="flex"
              justifyContent="space-around"
              rowGap="20px"
              p="20px"
            >
              {fusionResults?.map((response, index) => {
                const isCorrect =
                  response.responseData === response.expectedResponse;
                return (
                  <Grid
                    item
                    xs={12}
                    key={response.questionType}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap="4px"
                  >
                    <Typography
                      textAlign="center"
                      fontWeight={600}
                      fontSize="14px"
                    >
                      {index + 1}. {t(response.questionType)}
                    </Typography>
                    <Typography textAlign="center" fontSize="14px">
                      {`${t("word_response")}: ${getFormattedResponse(response.responseData)}`}
                    </Typography>
                    <Typography
                      textAlign="center"
                      fontSize="14px"
                      color={isCorrect ? "#13A321" : "#B21515"}
                    >
                      {`${t("word_expected_response")}: ${getFormattedResponse(response.expectedResponse)}`}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      )}

      {/* Past Stereopsis Responses */}
      {stereopsisResults?.length > 0 && (
        <Box
          border="1px solid black"
          borderRadius="8px"
          p="20px"
          sx={{ width: "100%", height: "100%" }}
        >
          <Typography mb="20px">{t("word_stereopsis_results")}</Typography>
          <Box display="flex" flexDirection="column" gap="20px">
            <Grid
              container
              display="flex"
              justifyContent="space-around"
              rowGap="20px"
              p="20px"
            >
              {stereopsisResults?.map((response, index) => {
                const isCorrect =
                  response.responseData === response.expectedResponse;
                return (
                  <Grid
                    item
                    xs={12}
                    key={response.questionType}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap="4px"
                  >
                    <Typography
                      textAlign="center"
                      fontWeight={600}
                      fontSize="14px"
                    >
                      {index + 1}. {t(response.questionType)}
                    </Typography>
                    <Typography textAlign="center" fontSize="14px">
                      {`${t("word_response")}: ${getFormattedResponse(response.responseData)}`}
                    </Typography>
                    <Typography
                      textAlign="center"
                      fontSize="14px"
                      color={isCorrect ? "#13A321" : "#B21515"}
                    >
                      {`${t("word_expected_response")}: ${getFormattedResponse(response.expectedResponse)}`}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Synoptophore;
